import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { loginUser, registerUser, loginAdmin, createServiceAgents } from '../utils/helpers';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

// Decode JWT payload without a library
const isTokenExpired = (token) => {
  if (!token) return true;
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp * 1000 < Date.now();
  } catch {
    return true;
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        // Check for user authentication
        const userToken = localStorage.getItem('userToken');
        const userData = localStorage.getItem('user');
        if (userToken && userData) {
          if (isTokenExpired(userToken)) {
            localStorage.removeItem('userToken');
            localStorage.removeItem('user');
          } else {
            setUser(JSON.parse(userData));
          }
        }

        // Check for admin authentication
        const adminToken = localStorage.getItem('adminToken');
        const adminData = localStorage.getItem('admin');
        if (adminToken && adminData) {
          if (isTokenExpired(adminToken)) {
            localStorage.removeItem('adminToken');
            localStorage.removeItem('admin');
          } else {
            setAdmin(JSON.parse(adminData));
          }
        }
      } catch (error) {
        console.error('Error initializing auth:', error);
        clearAuthData();
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();
  }, []);

  const clearAuthData = () => {
    localStorage.removeItem('userToken');
    localStorage.removeItem('adminToken');
    localStorage.removeItem('user');
    localStorage.removeItem('admin');
    setUser(null);
    setAdmin(null);
  };

  const login = async (credentials) => {
    try {
      const response = await loginUser(credentials);
      if (response.data.status === 'success') {
        const { user, token } = response.data.data;
        
        // Clear any existing admin session
        localStorage.removeItem('adminToken');
        localStorage.removeItem('admin');
        setAdmin(null);
        
        // Set user session
        localStorage.setItem('userToken', token);
        localStorage.setItem('user', JSON.stringify(user));
        setUser(user);
        
        return { success: true, user };
      }
      return { success: false, error: response.data.message };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.message || 'Login failed' 
      };
    }
  };

  const register = async (credentials) => {
    try {
      const response = await registerUser(credentials);
      if (response.data.status === 'success') {
        const { user, token } = response.data.data;
        
        // Clear any existing admin session
        localStorage.removeItem('adminToken');
        localStorage.removeItem('admin');
        setAdmin(null);
        
        // Set user session
        localStorage.setItem('userToken', token);
        localStorage.setItem('user', JSON.stringify(user));
        setUser(user);
        
        return { success: true, user };
      }
      return { success: false, error: response.data.message };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.message || 'Registration failed' 
      };
    }
  };

  const adminLogin = async (credentials) => {
    try {
      const response = await loginAdmin(credentials);
      if (response.data.status === 'success') {
        const { admin, token } = response.data.data;
        
        // Clear any existing user session
        localStorage.removeItem('userToken');
        localStorage.removeItem('user');
        setUser(null);
        
        // Set admin session
        localStorage.setItem('adminToken', token);
        localStorage.setItem('admin', JSON.stringify(admin));
        setAdmin(admin);
        
        return { success: true, admin };
      }
      return { success: false, error: response.data.message };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.message || 'Admin login failed' 
      };
    }
  };

  const createAdminAccount = async (credentials) => {
    try {
      // Assuming you have a createAdmin API function
      const response = await createServiceAgents(credentials); // or whatever your actual API function is
      if (response.data.status === 'success') {
        const { admin, token } = response.data.data;
        
        // Clear any existing user session
        localStorage.removeItem('userToken');
        localStorage.removeItem('user');
        setUser(null);
        
        // Set admin session
        localStorage.setItem('adminToken', token);
        localStorage.setItem('admin', JSON.stringify(admin));
        setAdmin(admin);
        
        return { success: true, admin };
      }
      return { success: false, error: response.data.message };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.message || 'Admin creation failed' 
      };
    }
  };

  const logout = () => {
    clearAuthData();
  };

  const updateAdmin = useCallback((adminData) => {
    localStorage.setItem('admin', JSON.stringify(adminData));
    setAdmin(adminData);
  }, []);

  const updateUser = useCallback((userData) => {
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
  }, []);

  // Helper function to get current user's role
  const getCurrentUserRole = () => {
    if (admin) return admin.role;
    if (user) return user.role;
    return null;
  };

  // Helper function to check if user is authenticated
  const isAuthenticated = () => {
    return !!(user || admin);
  };

  // Helper function to get current token
  const getCurrentToken = () => {
    return localStorage.getItem('adminToken') || localStorage.getItem('userToken');
  };

  const value = {
    // State
    admin,
    user,
    loading,
    
    // Authentication methods
    login,
    register,
    adminLogin,
    createAdmin: createAdminAccount, // Fixed naming
    logout,
    // Update methods
    updateUser,
    updateAdmin,
    // Helper methods
    getCurrentUserRole,
    isAuthenticated,
    getCurrentToken,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};